import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, AlertTriangle, Building2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: business-formation */
export default function BusinessFormation() {
  const { t } = useLanguage();
  const services = [{ en: "LLC Formation Guidance", ko: "LLC 설립 지원", detailEn: "Step-by-step guidance for forming your LLC in California, including Articles of Organization filing.", detailKo: "캘리포니아 LLC 설립을 위한 단계별 안내, Articles of Organization 신청 포함." },
    { en: "S-Corp Election Review", ko: "S-Corp 선택 검토", detailEn: "Evaluate whether S-Corp election makes sense for your business and handle Form 2553 filing.", detailKo: "S-Corp 선택이 비즈니스에 적합한지 평가하고 Form 2553 신청을 처리합니다." },
    { en: "EIN Application Support", ko: "EIN 신청 지원", detailEn: "Apply for your Employer Identification Number (EIN) with the IRS — the tax ID for your business.", detailKo: "IRS에 사업자 세금 식별 번호(EIN)를 신청합니다." },
    { en: "FBN / DBA Guidance", ko: "FBN / DBA 안내", detailEn: "Guidance on filing a Fictitious Business Name (FBN/DBA) with your county if operating under a trade name.", detailKo: "상호명으로 운영 시 카운티에 FBN/DBA 신청 안내." },
    { en: "Basic Compliance Checklist", ko: "기본 컴플라이언스 체크리스트", detailEn: "City business license, state registration, and basic annual compliance requirements.", detailKo: "시 사업자 허가증, 주 등록, 기본 연간 컴플라이언스 요건." },
    { en: "First-Year Tax Setup", ko: "첫 해 세금 셋업", detailEn: "Set up your accounting method, fiscal year, estimated tax schedule, and payroll setup (if applicable).", detailKo: "회계 방법, 회계연도, 예상세금 일정, 급여 셋업(해당 시)을 구성합니다." },
  ];

  const comparison = [
    { label: "Liability Protection", labelKo: "개인 책임 보호", soleProp: "None", llc: "Yes", sCorp: "Yes" },
    { label: "Self-Employment Tax", labelKo: "자영업세", soleProp: "Full (15.3%)", llc: "Full (15.3%)", sCorp: "On salary only" },
    { label: "Pass-Through Taxation", labelKo: "개인 과세 처리", soleProp: "Yes", llc: "Yes", sCorp: "Yes" },
    { label: "Payroll Required", labelKo: "급여 지급 의무", soleProp: "No", llc: "No", sCorp: "Yes (W-2)" },
    { label: "CA Annual Fee", labelKo: "CA 연간 수수료", soleProp: "None", llc: "$800+ min", sCorp: "$800+ min" },
    { label: "Complexity", labelKo: "복잡성", soleProp: "Simplest", llc: "Moderate", sCorp: "Most complex" },
  ];

  return (
    <>
      <SEO
        title="Business Formation Services San Jose | LLC & S Corp Setup | NexBiz Tax Advisor"
        description="Business Formation services in San Jose — LLC Formation, S Corp Election, EIN Application, and first-year tax setup. Enrolled Agent guidance for new businesses in the Bay Area."
      />
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("Business Formation", "회사 설립")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Business Formation Services", "비즈니스 설립 서비스")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "Choosing the right business structure can significantly impact taxes. We help clients evaluate LLC, S Corporation, and sole proprietorship options based on tax efficiency and compliance requirements.",
                "올바른 사업 구조 선택이 세금에 큰 영향을 미칩니다. LLC, S Corporation, 개인사업자 옵션을 세금 효율성과 컴플라이언스 기준으로 함께 평가합니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Entity Comparison Table */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-3">
              {t("Sole Prop vs. LLC vs. S-Corp", "개인사업자 vs. LLC vs. S-Corp")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
              {t("Understanding the differences is key to making the right decision.", "차이점을 이해하는 것이 올바른 결정을 내리는 핵심입니다.")}
            </motion.p>
            <motion.div variants={fadeUp} className="overflow-x-auto rounded-xl border border-border shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="px-5 py-4 text-left font-semibold">{t("Factor", "항목")}</th>
                    <th className="px-5 py-4 text-center font-semibold">{t("Sole Prop", "개인사업자")}</th>
                    <th className="px-5 py-4 text-center font-semibold">LLC</th>
                    <th className="px-5 py-4 text-center font-semibold bg-accent/30">S-Corp</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-muted/40"}>
                      <td className="px-5 py-3.5 font-medium text-foreground">{t(row.label, row.labelKo)}</td>
                      <td className="px-5 py-3.5 text-center text-muted-foreground">{row.soleProp}</td>
                      <td className="px-5 py-3.5 text-center text-muted-foreground">{row.llc}</td>
                      <td className="px-5 py-3.5 text-center text-primary font-medium">{row.sCorp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
            <motion.p variants={fadeUp} className="text-xs text-muted-foreground text-center mt-3">
              {t("This is a general comparison. Individual situations vary. Consult with us for a personalized assessment.", "이것은 일반적인 비교입니다. 개별 상황은 다릅니다. 개인 맞춤 평가를 위해 상담하세요.")}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-muted py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("Formation Services We Provide", "제공하는 설립 서비스")}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {services.map((s, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-xl border border-border p-5 hover:border-accent/40 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t(s.en, s.ko)}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t(s.detailEn, s.detailKo)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-10 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="flex items-start gap-4 p-5 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 mb-1">{t("Important Notice", "중요 공지")}</p>
                <p className="text-sm text-amber-700 leading-relaxed">
                  {t(
                    "Business formation support at NexBiz is tax-focused and does not replace legal advice from a licensed attorney. For legal contracts, operating agreements, or compliance matters beyond tax, please consult a business attorney.",
                    "NexBiz의 회사 설립 지원은 세금 중심이며 공인 변호사의 법적 조언을 대체하지 않습니다. 세금 이외의 법적 계약, 운영 협약 또는 컴플라이언스 사항은 사업 변호사와 상담하세요."
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-4">
              <Building2 className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold text-foreground">{t("Ready to Start Your Business?", "비즈니스를 시작할 준비가 되셨나요?")}</h2>
            </motion.div>
            <motion.p variants={fadeUp} className="text-muted-foreground mb-8">
              {t("Let's build the right foundation — together.", "올바른 기반을 함께 만들어나갑시다.")}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-md">
                {t("Schedule a Consultation", "상담 예약하기")} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
